import clsx from 'clsx'
import { ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { MovementItem } from '@/types/dashboards'
import { useDrillDown } from './useDrillDown'

/** Compact relative age — the rail is scanned, not read. */
function relativeAge(iso: string): string {
  const at = Date.parse(iso)
  if (!Number.isFinite(at)) return '--'
  const minutes = Math.max(0, Math.round((Date.now() - at) / 60_000))
  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.round(hours / 24)}d`
}

interface MovementRailProps {
  movements: MovementItem[]
  className?: string
}

/** Recent Movement — the most recent status transitions across the board. */
export default function MovementRail({ movements, className }: MovementRailProps) {
  const navigate = useNavigate()
  const { open } = useDrillDown()

  if (movements.length === 0) {
    return (
      <p className={clsx('text-[11px] text-stone-500', className)}>
        No status changes recorded in this window.
      </p>
    )
  }

  return (
    <ul className={clsx('space-y-1.5', className)}>
      {movements.map((movement) => {
        const clickable = Boolean(movement.drill || movement.href)
        return (
          <li key={movement.id} className="flex items-start gap-2">
            <span className="mt-0.5 w-7 flex-shrink-0 font-mono text-[9px] uppercase tracking-wider text-stone-600">
              {relativeAge(movement.occurredAt)}
            </span>
            <div className="min-w-0 flex-1">
              {clickable ? (
                <button
                  type="button"
                  onClick={() =>
                    movement.drill ? open(movement.drill) : navigate(movement.href as string)
                  }
                  className="text-left text-[11px] leading-snug text-stone-200 underline decoration-amber-400/20 decoration-dotted underline-offset-[3px] transition-colors hover:text-amber-200 hover:decoration-amber-300/70 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50"
                >
                  {movement.title}
                </button>
              ) : (
                <span className="text-[11px] leading-snug text-stone-200">{movement.title}</span>
              )}
              {(movement.from || movement.to) && (
                <div className="mt-0.5 flex items-center gap-1 text-[10px] text-stone-500">
                  <span className="capitalize">{movement.from ?? '--'}</span>
                  <ArrowRight size={9} className="text-stone-600" />
                  <span className="capitalize text-stone-300">{movement.to ?? '--'}</span>
                </div>
              )}
              {movement.detail && (
                <div className="mt-0.5 text-[10px] leading-relaxed text-stone-500">
                  {movement.detail}
                </div>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
